import React, { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { NOTIFICATION_KINDS } from '../../constants/shipments';
import { relativeTime, formatDateTime } from '../../utils/shipmentsFormat';
import { shipmentsApi } from '../../utils/shipmentsApi';
import { useRemote } from './useRemote';

const LIMIT = 20;

// Bell with an unread badge and a dropdown with the latest notifications.
// `unread` comes from GET /summary; `onChanged()` makes the page reload it after a notification was read.
const NotificationBell = ({ unread, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(0);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const panelId = useId();
  const { data, error, loading, reload } = useRemote(open ? `n${opened}` : null, () => shipmentsApi.notifications({ limit: LIMIT }));

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const toggle = () => {
    if (!open) { setOpened((n) => n + 1); setActionError(''); }
    setOpen((o) => !o);
  };

  const markRead = async (item) => {
    if (item.readAt) return;
    try { await shipmentsApi.notificationRead(item.id); onChanged?.(); } catch { /* the link still works */ }
  };

  const noUnread = (data?.unread ?? (Number(unread) || 0)) === 0;
  const markAll = async () => {
    if (busy || noUnread) return; // aria-disabled instead of disabled: the button keeps the focus
    setBusy(true);
    setActionError('');
    try {
      await shipmentsApi.notificationsReadAll();
      reload();
      onChanged?.();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); btnRef.current?.focus(); }
  };
  const onBlur = (e) => { if (!wrapRef.current?.contains(e.relatedTarget)) setOpen(false); };

  const count = Number(unread) || 0;
  const items = data?.items || [];

  return (
    <div ref={wrapRef} className="shp-pop-wrap" onKeyDown={onKeyDown} onBlur={onBlur}>
      <button
        ref={btnRef} type="button" className="shp-iconbtn shp-iconbtn--border" style={{ width: 44, height: 44 }}
        aria-haspopup="true" aria-expanded={open} aria-controls={open ? panelId : undefined}
        aria-label={count > 0 ? `Oznámení, nepřečtených: ${count}` : 'Oznámení'} onClick={toggle}
      >
        <Bell size={20} aria-hidden="true" />
        {count > 0 && <span className="shp-count" style={{ position: 'absolute', top: -6, right: -6 }} aria-hidden="true">{count > 99 ? '99+' : count}</span>}
      </button>
      {open && (
        <div id={panelId} role="region" aria-label="Oznámení" className="shp-pop shp-pop--right" style={{ width: 'min(380px, calc(100vw - 32px))', padding: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 12px', borderBottom: '1px solid #F0F0F0' }}>
            <strong style={{ fontSize: 14 }}>Oznámení</strong>
            <button type="button" className="shp-textbtn" onClick={markAll} aria-disabled={busy || noUnread}>
              <CheckCheck size={15} aria-hidden="true" /> Označit vše jako přečtené
            </button>
          </div>
          {actionError && <p role="alert" className="shp-error" style={{ padding: '8px 12px' }}>{actionError}</p>}
          {error && <p role="alert" className="shp-error" style={{ padding: '12px' }}>{error.message}</p>}
          {!error && loading && !data && <p role="status" className="shp-muted" style={{ padding: 12, margin: 0 }}>Načítám…</p>}
          {!error && data && items.length === 0 && <p className="shp-muted" style={{ padding: 16, margin: 0, textAlign: 'center' }}>Zatím žádná oznámení.</p>}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {items.map((n) => {
              const body = (
                <>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666' }}>
                    {!n.readAt && <span className="shp-dot" aria-hidden="true" />}
                    {!n.readAt && <span className="shp-sr">Nepřečteno: </span>}
                    <span>{NOTIFICATION_KINDS[n.kind] || 'Oznámení'}</span>
                    <time dateTime={n.createdAt} title={formatDateTime(n.createdAt)} style={{ marginLeft: 'auto' }}>{relativeTime(n.createdAt)}</time>
                  </span>
                  <span className="shp-wrap" dir="auto" style={{ fontSize: 14, color: '#333', fontWeight: n.readAt ? 400 : 600 }}>{n.message}</span>
                </>
              );
              const style = { display: 'grid', gap: 4, padding: '10px 12px', borderBottom: '1px solid #F5F5F5', textDecoration: 'none', color: 'inherit', width: '100%', textAlign: 'left', background: n.readAt ? 'transparent' : '#FFF8F8', border: 'none', font: 'inherit', cursor: 'pointer' };
              const shipmentId = Number(n.shipmentId);
              return (
                <li key={n.id}>
                  {Number.isInteger(shipmentId) && shipmentId > 0 ? (
                    <Link to={`/zasilky/${shipmentId}`} style={style} onClick={() => { markRead(n); setOpen(false); }}>{body}</Link>
                  ) : (
                    <button type="button" style={style} onClick={() => markRead(n)}>{body}</button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
